import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Trophy, 
  Clock, 
  Users, 
  Gift, 
  Star, 
  ChevronRight,
  Calendar,
  Gamepad2,
  Crown,
  Timer,
  Target,
  ArrowLeft,
  LogIn,
  UserPlus,
  CheckCircle,
  Handshake,
  ExternalLink,
  Play,
  User,
  Lock,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import api from '../../lib/axios';

// Matrix Rain Effect Component - Casa de Papel Theme
const MatrixRain = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789€$£¥₿#@&%?!';
    const charArray = chars.split('');
    const fontSize = 14;
    const columns = canvas.width / fontSize;
    
    const drops = [];
    for (let i = 0; i < columns; i++) {
      drops[i] = Math.random() * -100;
    }
    
    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = `${fontSize}px monospace`;
      
      for (let i = 0; i < drops.length; i++) {
        const char = charArray[Math.floor(Math.random() * charArray.length)];
        const brightness = Math.random();
        if (brightness > 0.95) {
          ctx.fillStyle = '#ffffff';
        } else if (brightness > 0.7) {
          ctx.fillStyle = '#ff3333';
        } else {
          ctx.fillStyle = '#8b0000';
        }
        ctx.fillText(char, i * fontSize, drops[i] * fontSize);
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    };
    
    const interval = setInterval(draw, 50);
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0 opacity-15"
      style={{ mixBlendMode: 'screen' }}
    />
  );
};

const GamePublicPage = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canPlay, setCanPlay] = useState({ can_play: true, reason: null, score: null });

  useEffect(() => {
    fetchData();
    checkPlayerSession();
  }, [gameId]);

  const checkPlayerSession = async () => {
    const token = localStorage.getItem('game_token');
    if (token) {
      try {
        const res = await api.get(`/game/players/me?token=${token}`);
        setPlayer(res.data);
        // Check if can play
        const canPlayRes = await api.get(`/game/contests/${gameId}/can-play?token=${token}`);
        setCanPlay(canPlayRes.data);
      } catch (e) {
        localStorage.removeItem('game_token');
      }
    }
  };

  const fetchData = async () => {
    try {
      const [gameRes, leaderboardRes] = await Promise.all([
        api.get(`/game/contests/${gameId}`),
        api.get(`/game/leaderboard/${gameId}?limit=20`)
      ]);
      setGame(gameRes.data);
      setLeaderboard(leaderboardRes.data);
    } catch (e) {
      console.error('Error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('game_token');
    setPlayer(null);
    setCanPlay({ can_play: true, reason: null, score: null });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center relative overflow-hidden">
        <MatrixRain />
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500 relative z-10"></div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center relative overflow-hidden">
        <MatrixRain />
        <Card className="max-w-md bg-black border-2 border-red-500/50 relative z-10">
          <CardContent className="text-center py-12">
            <Trophy className="w-16 h-16 text-red-500/50 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Jeu introuvable</h2>
            <p className="text-white/60 mb-4">Ce jeu concours n'existe pas ou a été supprimé.</p>
            <Link to="/le-coffre-msc">
              <Button className="bg-red-600 hover:bg-red-700">Retour au Coffre MSC</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <MatrixRain />
      <div className="fixed inset-0 bg-gradient-to-b from-red-900/20 via-black/30 to-red-900/20 pointer-events-none z-0" />
      
      {/* Header */}
      <header className="border-b border-red-500/30 bg-black/90 backdrop-blur-sm sticky top-0 z-50 relative">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/le-coffre-msc" className="flex items-center gap-2 text-red-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Le Coffre MSC</span>
          </Link>
          {player ? (
            <div className="flex items-center gap-3">
              <span className="text-white/60 text-sm hidden sm:block">Bienvenue,</span>
              <Link 
                to="/mon-espace-joueur" 
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-950 hover:bg-red-900 transition-colors border border-red-500/30"
              >
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center text-white text-xs font-bold">
                  {player.first_name?.[0]?.toUpperCase()}
                </div>
                <span className="text-white text-sm font-medium">{player.first_name}</span>
                <Badge className="bg-red-600 text-xs">{player.total_score} pts</Badge>
              </Link>
            </div>
          ) : (
            <Link 
              to="/mon-espace-joueur" 
              className="flex items-center gap-2 text-red-400 hover:text-white transition-colors text-sm"
            >
              <User className="w-4 h-4" />
              Se connecter
            </Link>
          )}
        </div>
      </header>

      {/* Hero compact avec CTA principal */}
      <section className="pt-8 pb-4 px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-6">
            {game.is_main_game && (
              <Badge className="bg-gradient-to-r from-red-600 to-red-700 mb-3">
                <Crown className="w-3 h-3 mr-1" />
                Le Coffre MSC
              </Badge>
            )}
            
            {game.month && (
              <Badge className="bg-red-600 mb-3 ml-2">
                Quiz Mensuel - Mois {game.month}
              </Badge>
            )}
            
            {/* Partnership Badge */}
            {game.is_partnership && (
              <Badge className="bg-blue-600 mb-3 ml-2">
                <Handshake className="w-3 h-3 mr-1" />
                En partenariat avec {game.partner_name}
              </Badge>
            )}
            
            {/* Game Icon - smaller */}
            {game.is_partnership && game.partner_logo_url ? (
              <div className="mb-4">
                <img 
                  src={game.partner_logo_url} 
                  alt={game.partner_name}
                  className="h-16 mx-auto object-contain rounded-lg"
                />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center mx-auto mb-4 shadow-2xl shadow-red-500/30">
                <Gamepad2 className="w-8 h-8 text-white" />
              </div>
            )}
            
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{game.title}</h1>
            <p className="text-white/70 max-w-xl mx-auto text-sm">{game.description}</p>
          </div>

          {/* ===== CTA PRINCIPAL - TOUJOURS VISIBLE EN HAUT ===== */}
          <div className="bg-gradient-to-r from-red-900/50 via-red-800/50 to-red-900/50 border-2 border-red-500/50 rounded-2xl p-6 mb-6 shadow-xl shadow-red-500/20">
            {player ? (
              <div className="space-y-4">
                {canPlay.can_play ? (
                  <div className="text-center">
                    <p className="text-white/80 mb-4">Prêt à jouer, <span className="text-red-400 font-semibold">{player.first_name}</span> ?</p>
                    <Link to={`/jeu/${gameId}/jouer`} className="inline-block">
                      <Button size="lg" className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-lg shadow-red-500/40 text-lg px-8 py-6 h-auto animate-pulse">
                        <Gamepad2 className="w-6 h-6 mr-3" />
                        JOUER MAINTENANT
                        <ChevronRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                  </div>
                ) : canPlay.reason === 'already_played' && canPlay.passed ? (
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                      <span className="text-green-400 font-bold text-xl">Quiz validé !</span>
                    </div>
                    <p className="text-white text-lg">Votre score : <span className="font-bold text-green-400">{canPlay.score} pts</span></p>
                    <p className="text-white/60 text-sm mt-2">Vous avez obtenu le caractère de ce mois ✓</p>
                  </div>
                ) : canPlay.reason === 'already_played' && !canPlay.passed ? (
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <AlertCircle className="w-6 h-6 text-red-400" />
                      <span className="text-red-400 font-semibold">Quiz non validé ({canPlay.score} pts)</span>
                    </div>
                    <p className="text-white/60 text-sm mb-4">Vous n'avez pas atteint 50% de bonnes réponses</p>
                    <Link to={`/jeu/${gameId}/jouer`} className="inline-block">
                      <Button size="lg" className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-lg shadow-red-500/40">
                        <RefreshCw className="w-5 h-5 mr-2" />
                        Retenter le quiz
                      </Button>
                    </Link>
                  </div>
                ) : canPlay.reason === 'game_finished' ? (
                  <div className="text-center">
                    <p className="text-white/60">Ce jeu est terminé</p>
                  </div>
                ) : null}
                
                <div className="flex justify-center">
                  <Button variant="ghost" size="sm" className="text-white/50 hover:text-white hover:bg-red-900/50" onClick={handleLogout}>
                    Déconnexion
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center space-y-4">
                <p className="text-white/80 mb-2">Connectez-vous pour participer au quiz !</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to={`/jeu/${gameId}/inscription`}>
                    <Button size="lg" className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-lg shadow-red-500/40">
                      <UserPlus className="w-5 h-5 mr-2" />
                      S'inscrire gratuitement
                    </Button>
                  </Link>
                  <Link to={`/jeu/${gameId}/connexion`}>
                    <Button size="lg" variant="outline" className="border-red-500/50 text-white hover:bg-red-950">
                      <LogIn className="w-5 h-5 mr-2" />
                      Se connecter
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* YouTube Video */}
          {game.youtube_video_url && (
            <div className="mb-6">
              <div className="aspect-video max-w-2xl mx-auto rounded-2xl overflow-hidden shadow-2xl shadow-red-500/20 border border-red-500/30">
                <iframe
                  src={game.youtube_video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                  title="Présentation du jeu"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}

          {/* Partner Description */}
          {game.is_partnership && game.partner_description && (
            <div className="mb-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 max-w-xl mx-auto">
              <p className="text-blue-300 text-sm">{game.partner_description}</p>
              {game.partner_website && (
                <a 
                  href={game.partner_website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm mt-2"
                >
                  Visiter le site partenaire <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          )}

          {/* Game info - compact */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <Card className="bg-red-950/60 border border-red-500/20">
              <CardContent className="p-3 text-center">
                <Gift className="w-6 h-6 text-red-400 mx-auto mb-1" />
                <p className="text-xs text-white/60">Lot</p>
                <p className="font-bold text-white text-sm truncate">{game.prize}</p>
              </CardContent>
            </Card>
            <Card className="bg-red-950/60 border border-red-500/20">
              <CardContent className="p-3 text-center">
                <Timer className="w-6 h-6 text-red-400 mx-auto mb-1" />
                <p className="text-xs text-white/60">Temps/question</p>
                <p className="font-bold text-white text-sm">{game.time_limit_seconds}s</p>
              </CardContent>
            </Card>
            <Card className="bg-red-950/60 border border-red-500/20">
              <CardContent className="p-3 text-center">
                <Star className="w-6 h-6 text-yellow-400 mx-auto mb-1" />
                <p className="text-xs text-white/60">Points/réponse</p>
                <p className="font-bold text-white text-sm">{game.points_per_correct} pts</p>
              </CardContent>
            </Card>
          </div>

          {/* Info classement annuel */}
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3 mb-4 text-center">
            <p className="text-sm text-white/80">
              <Trophy className="w-4 h-4 text-yellow-400 inline mr-1" />
              <strong className="text-yellow-400">Classement annuel</strong> - Vos points s'accumulent jusqu'au 31 décembre !
            </p>
          </div>

          {/* Info caractère mensuel */}
          {game.month && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3 mb-6 text-center">
              <p className="text-sm text-white/80">
                <Lock className="w-4 h-4 text-green-400 inline mr-1" />
                <strong className="text-green-400">Caractère du mois {game.month}</strong> - Validez avec ≥50% de bonnes réponses !
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Leaderboard */}
      <section className="py-8 px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-red-400" />
            Classement du jeu
          </h2>

          <div className="bg-black rounded-2xl border-2 border-red-500/30 overflow-hidden shadow-xl shadow-red-500/10">
            <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-red-950/50 text-sm font-medium text-white/60">
              <div className="col-span-1">#</div>
              <div className="col-span-5">Joueur</div>
              <div className="col-span-3 text-center">Bonnes réponses</div>
              <div className="col-span-3 text-right">Score</div>
            </div>
            
            {leaderboard.length === 0 ? (
              <div className="px-6 py-12 text-center text-white/40">
                Aucun joueur pour le moment. Soyez le premier !
              </div>
            ) : (
              leaderboard.map((entry, idx) => (
                <div 
                  key={entry.id || idx}
                  className={`grid grid-cols-12 gap-4 px-6 py-4 border-t border-red-500/20 ${idx < 3 ? 'bg-gradient-to-r from-transparent' : ''} ${idx === 0 ? 'via-yellow-500/10 to-transparent' : idx === 1 ? 'via-gray-400/10 to-transparent' : idx === 2 ? 'via-orange-500/10 to-transparent' : ''}`}
                >
                  <div className="col-span-1 flex items-center">
                    {idx === 0 ? (
                      <span className="text-2xl">🥇</span>
                    ) : idx === 1 ? (
                      <span className="text-2xl">🥈</span>
                    ) : idx === 2 ? (
                      <span className="text-2xl">🥉</span>
                    ) : (
                      <span className="text-white/50 font-medium">{entry.rank}</span>
                    )}
                  </div>
                  <div className="col-span-5 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center text-white font-bold">
                      {(entry.display_name || entry.player_name)?.[0]?.toUpperCase()}
                    </div>
                    <span className="text-white font-medium">{entry.display_name || entry.player_name}</span>
                  </div>
                  <div className="col-span-3 flex items-center justify-center text-white/60">
                    {entry.correct_answers} / {entry.total_questions}
                  </div>
                  <div className="col-span-3 flex items-center justify-end">
                    <span className="text-red-400 font-bold">{entry.score?.toLocaleString()}</span>
                    <span className="text-white/40 ml-1">pts</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-red-500/20 relative z-10">
        <div className="max-w-4xl mx-auto text-center text-white/40 text-sm">
          <p>© {new Date().getFullYear()} - Le Coffre MSC - MSC L'Agence</p>
          <Link to={`/jeu/${gameId}/reglement`} className="text-red-400 hover:underline">
            Voir le règlement complet
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default GamePublicPage;
